<script setup lang="ts">
import { computed } from 'vue'
import ConfirmDialog from 'primevue/confirmdialog'
import GbButton from '@/components/GbButton.vue'
import { vFocusOnMount } from '@/util/focus_on_mount'

const props = defineProps<{ group: string }>()

const headerId = computed(() => `${props.group}-header`)
// The container slot replaces PrimeVue's header, so the dialog is named by ours instead.
const passThrough = computed(() => ({ root: { 'aria-labelledby': headerId.value } }))

// PrimeVue keeps OK live while the dialog animates out, so a double click would accept twice.
const acceptedMessages = new WeakSet<object>()
function acceptOnce(message: object, acceptCallback: () => void) {
  if (acceptedMessages.has(message)) return
  acceptedMessages.add(message)
  acceptCallback()
}
</script>

<template>
  <ConfirmDialog :group="group" :pt="passThrough" class="w-auto md:w-[38rem]">
    <template #container="{ message, acceptCallback, rejectCallback }">
      <div class="flex flex-col rounded-md bg-surface-50">
        <div class="mt-2 border-b border-gray-300 p-3">
          <span :id="headerId" class="block pl-3 text-xl font-medium">{{ message.header }}</span>
        </div>
        <p class="flex items-start gap-2 py-5 pl-7 pr-12" :class="{ 'text-red-300': message.icon }">
          <span v-if="message.icon" class="material-icons-round" aria-hidden="true">{{ message.icon }}</span>
          {{ message.message }}
        </p>
        <div class="flex items-center justify-end gap-2 border-t border-gray-300 p-4">
          <GbButton :label="message.rejectLabel ?? 'Cancel'" gbTheme="grayOutlineSecondary" @click="rejectCallback" />
          <GbButton v-focus-on-mount :label="message.acceptLabel ?? 'OK'" gbTheme="bluePrimary" @click="acceptOnce(message, acceptCallback)" />
        </div>
      </div>
    </template>
  </ConfirmDialog>
</template>
